import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Plus, Shield, Check, Loader2, Lock, Users, Building2 } from 'lucide-react';
import { Input } from './ui/input';
import { Label } from './ui/label';
import ServiceToggle from './ServiceToggle';
import TabPageLayout from './TabPageLayout';
import EmptyState from './EmptyState';
import { PrimaryButton, SecondaryButton, SuccessButton, LayoutActionButton } from './Buttons';
import StandardModal from './StandardModal';
import ResourceCard from './ResourceCard';
import ModalTabSelector from './ModalTabSelector';
import DeleteConfirmModal from './DeleteConfirmModal';
import { BACKEND_URL } from '../config/api';
import { SUPER_ADMIN_TABS, logAdminEvent } from '../utils/superAdmin';

const PLATFORM_API = `${BACKEND_URL}/api/v2/super-admin`;
const authHeaders = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('jwtToken')}` } });

const PRESET_COLORS = [
  '#3B82F6', '#10B981', '#F59E0B', '#EF4444',
  '#8B5CF6', '#EC4899', '#14B8A6', '#F97316',
];

const emptyForm = {
  name: '',
  description: '',
  color: '#3B82F6',
  tab_permissions: {},
  scope_own_orgs: false,
};

const SuperAdminRolesTab = () => {
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingRole, setEditingRole] = useState(null);
  const [modalStep, setModalStep] = useState(1);
  const [formData, setFormData] = useState(emptyForm);
  const [isSaving, setIsSaving] = useState(false);

  const [roleToDelete, setRoleToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const fetchRoles = useCallback(async () => {
    try {
      const res = await axios.get(`${PLATFORM_API}/roles`, authHeaders());
      setRoles(res.data.roles || []);
    } catch (err) {
      toast.error('Failed to load roles');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchRoles(); }, [fetchRoles]);

  const handleOpenDialog = (role = null) => {
    if (role) {
      setEditingRole(role);
      setFormData({
        name: role.name,
        description: role.description || '',
        color: role.color || '#3B82F6',
        tab_permissions: role.tab_permissions || {},
        scope_own_orgs: !!role.scope_own_orgs,
      });
    } else {
      setEditingRole(null);
      setFormData(emptyForm);
    }
    setModalStep(1);
    setDialogOpen(true);
  };

  const handleSaveRole = async () => {
    if (!formData.name.trim()) { toast.error('Role name is required'); return; }
    setIsSaving(true);
    try {
      if (editingRole) {
        await axios.patch(`${PLATFORM_API}/roles/${editingRole.id}`, formData, authHeaders());
        toast.success('Role updated');
      } else {
        await axios.post(`${PLATFORM_API}/roles`, formData, authHeaders());
        toast.success('Role created');
      }
      setDialogOpen(false);
      fetchRoles();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save role');
    } finally {
      setIsSaving(false);
    }
  };

  const handleDeleteRole = async () => {
    setIsDeleting(true);
    try {
      await axios.delete(`${PLATFORM_API}/roles/${roleToDelete.id}`, authHeaders());
      toast.success('Role deleted');
      setRoleToDelete(null);
      fetchRoles();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete role');
    } finally {
      setIsDeleting(false);
    }
  };

  const setPerm = (tabId, key, value) => {
    setFormData(f => {
      const perms = { ...(f.tab_permissions || {}) };
      const cur = { read: false, manage: false, ...(perms[tabId] || {}) };
      cur[key] = value;
      if (key === 'manage' && value) cur.read = true;
      if (key === 'read' && !value) cur.manage = false;
      perms[tabId] = cur;
      return { ...f, tab_permissions: perms };
    });
  };

  const permsSummary = (role) => {
    const enabled = SUPER_ADMIN_TABS
      .filter(t => role.tab_permissions?.[t.id]?.read)
      .map(t => t.label);
    return enabled.length ? enabled.join(', ') : 'No access';
  };

  const isLocked = (role) => role.is_system && role.name.toLowerCase() === 'owner';

  return (
    <TabPageLayout
      title="Roles"
      subtitle="Define what each Platform Admin team member can see and do"
      loading={loading}
      actions={
        <LayoutActionButton icon={Plus} onClick={() => handleOpenDialog()} data-testid="superadmin-roles-add-button">
          Add
        </LayoutActionButton>
      }
    >
      <div className="space-y-6" data-testid="superadmin-roles-tab">
        {roles.length === 0 ? (
          <EmptyState
            icon={Shield}
            title="No Roles Yet"
            description="Create a role to control team access"
            actionLabel="Create First Role"
            onAction={() => handleOpenDialog()}
          />
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {roles.map((role) => (
              <ResourceCard
                key={role.id}
                id={role.id}
                name={role.name}
                icon={Shield}
                iconColor={role.color || '#3B82F6'}
                topRightBadge={role.is_system ? { label: 'System', color: '#6B7280' } : undefined}
                descriptionLines={[
                  { icon: <Lock className="w-3 h-3" />, text: permsSummary(role) },
                  ...(role.scope_own_orgs ? [{ icon: <Building2 className="w-3 h-3" />, text: 'Only their companies' }] : []),
                ]}
                metric={{ label: 'Members', value: String(role.member_count || 0), color: '#111827' }}
                onEdit={() => handleOpenDialog(role)}
                onDelete={isLocked(role) ? undefined : () => setRoleToDelete(role)}
              />
            ))}
          </div>
        )}

        {/* Create / Edit Role Modal */}
        <StandardModal
          isOpen={dialogOpen}
          onClose={() => setDialogOpen(false)}
          title={editingRole ? `Edit ${editingRole.name}` : 'Create New Role'}
          size="md"
          footer={
            <>
              {modalStep === 2 && (
                <SecondaryButton onClick={() => setModalStep(1)} className="flex-1" disabled={isSaving}>Back</SecondaryButton>
              )}
              {modalStep === 1 ? (
                <>
                  <SecondaryButton onClick={() => setDialogOpen(false)} className="flex-1">Cancel</SecondaryButton>
                  <PrimaryButton onClick={() => setModalStep(2)} className="flex-1" disabled={!formData.name.trim()} data-testid="role-modal-next">
                    Next
                  </PrimaryButton>
                </>
              ) : (
                <SuccessButton onClick={handleSaveRole} className="flex-1" disabled={isSaving} data-testid="role-modal-save">
                  {isSaving ? (<><Loader2 className="w-4 h-4 mr-2 animate-spin" />Saving...</>) : (<><Check className="w-4 h-4 mr-2" />{editingRole ? 'Update Role' : 'Create Role'}</>)}
                </SuccessButton>
              )}
            </>
          }
        >
          {editingRole && (
            <div className="mb-5">
              <ModalTabSelector
                tabs={[
                  { value: 1, label: 'Role', icon: Shield },
                  { value: 2, label: 'Permissions', icon: Lock },
                ]}
                value={modalStep}
                onChange={setModalStep}
              />
            </div>
          )}

          {modalStep === 1 && (
            <div className="space-y-5">
              <div className="flex items-center gap-3 rounded-2xl bg-gray-50/70 px-4 py-3.5">
                <div className="w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0" style={{ backgroundColor: `${formData.color}14` }}>
                  <Shield className="w-5 h-5" style={{ color: formData.color }} />
                </div>
                <div className="min-w-0">
                  <p className="text-[15px] font-medium text-gray-900 tracking-tight truncate">{formData.name.trim() || 'New role'}</p>
                  <p className="text-xs text-gray-500 truncate">{formData.description?.trim() || 'Set a name, color & permissions'}</p>
                </div>
              </div>

              <div>
                <Label>Role Name <span className="text-red-500">*</span></Label>
                <Input
                  placeholder="e.g., Partner, Support, Ops"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="mt-1.5"
                  disabled={editingRole?.is_system && editingRole?.name?.toLowerCase() === 'owner'}
                  data-testid="role-name-input"
                />
              </div>
              <div>
                <Label>Description (Optional)</Label>
                <Input
                  placeholder="Brief description of this role"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  className="mt-1.5"
                  data-testid="role-description-input"
                />
              </div>
              <div>
                <Label>Color</Label>
                <div className="flex gap-2.5 flex-wrap mt-2 px-0.5 py-1.5">
                  {PRESET_COLORS.map((color) => (
                    <button
                      key={color}
                      type="button"
                      onClick={() => setFormData({ ...formData, color })}
                      className={`w-9 h-9 rounded-full flex items-center justify-center transition-all duration-150 hover:scale-110 ${formData.color === color ? 'ring-2 ring-offset-2 ring-gray-900/70 scale-110' : ''}`}
                      style={{ backgroundColor: color }}
                      aria-label={`Select color ${color}`}
                    >
                      {formData.color === color && <Check className="w-4 h-4 text-white" strokeWidth={3} />}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}

          {modalStep === 2 && (
            <div className="space-y-3">
              {/* Scope toggle */}
              <div className="flex items-center justify-between px-3.5 py-3 rounded-xl border border-gray-100 bg-gray-50/60">
                <div>
                  <p className="text-sm font-medium text-gray-900 flex items-center gap-1.5">
                    <Building2 className="w-4 h-4 text-blue-600" />Only their companies
                  </p>
                  <p className="text-xs text-gray-500 mt-0.5">Members only see data for companies they onboarded (partners)</p>
                </div>
                <ServiceToggle
                  checked={formData.scope_own_orgs}
                  onChange={(v) => setFormData({ ...formData, scope_own_orgs: v })}
                  data-testid="role-scope-switch"
                />
              </div>

              {/* Quick actions */}
              <div className="flex items-center gap-2 p-2.5 rounded-xl border border-gray-200 bg-gray-50">
                <span className="text-xs font-medium text-gray-500 flex-shrink-0">Quick:</span>
                <div className="flex gap-1.5 flex-wrap">
                  <button type="button" onClick={() => {
                    const p = {};
                    SUPER_ADMIN_TABS.forEach(t => { p[t.id] = { read: true, manage: false }; });
                    setFormData({ ...formData, tab_permissions: p });
                  }} className="px-2 py-1 bg-blue-50 border border-blue-200 text-blue-700 text-xs rounded-md hover:bg-blue-100 transition-colors">
                    View Only
                  </button>
                  <button type="button" onClick={() => {
                    const p = {};
                    SUPER_ADMIN_TABS.forEach(t => { p[t.id] = { read: true, manage: true }; });
                    setFormData({ ...formData, tab_permissions: p });
                  }} className="px-2 py-1 bg-green-50 border border-green-200 text-green-700 text-xs rounded-md hover:bg-green-100 transition-colors">
                    Full Access
                  </button>
                  <button type="button" onClick={() => setFormData({ ...formData, tab_permissions: {} })}
                    className="px-2 py-1 bg-white border border-gray-200 text-gray-500 text-xs rounded-md hover:bg-gray-100 transition-colors">
                    Clear
                  </button>
                </div>
              </div>

              {/* Per-tab permissions */}
              <div className="space-y-1.5">
                {SUPER_ADMIN_TABS.map(tab => {
                  const perms = formData.tab_permissions?.[tab.id] || {};
                  return (
                    <div key={tab.id} className={`px-3.5 py-3 rounded-xl transition-all ${perms.read ? 'bg-white shadow-[0_2px_10px_rgba(0,0,0,0.04)]' : 'bg-gray-50'}`}>
                      <div className="flex items-center justify-between gap-2">
                        <span className={`text-[15px] font-medium ${perms.read ? 'text-gray-900' : 'text-gray-400'}`}>{tab.label}</span>
                        <div className="flex items-center gap-5 flex-shrink-0">
                          <label className="flex items-center gap-1.5 cursor-pointer">
                            <input
                              type="checkbox"
                              checked={!!perms.read}
                              onChange={(e) => setPerm(tab.id, 'read', e.target.checked)}
                              className="w-4 h-4 rounded border-gray-300 text-blue-600"
                              data-testid={`role-perm-${tab.id}-read`}
                            />
                            <span className="text-xs text-gray-500">View</span>
                          </label>
                          <label className="flex items-center gap-1.5 cursor-pointer">
                            <input
                              type="checkbox"
                              checked={!!perms.manage}
                              onChange={(e) => setPerm(tab.id, 'manage', e.target.checked)}
                              className="w-4 h-4 rounded border-gray-300 text-green-600"
                              data-testid={`role-perm-${tab.id}-manage`}
                            />
                            <span className="text-xs text-gray-500">Manage</span>
                          </label>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </StandardModal>

        <DeleteConfirmModal
          isOpen={!!roleToDelete}
          onClose={() => setRoleToDelete(null)}
          onConfirm={handleDeleteRole}
          title="Delete Role?"
          description={`Are you sure you want to delete "${roleToDelete?.name}"?`}
          isDeleting={isDeleting}
        >
          <p className="font-medium mb-1">This action cannot be undone.</p>
          <p>Roles that still have members assigned cannot be deleted.</p>
        </DeleteConfirmModal>
      </div>
    </TabPageLayout>
  );
};

export default SuperAdminRolesTab;
